'use strict';

/* =====================================================
   ONI SITES — ANIMATION ENGINE (optimised)
   ===================================================== */

function initCursor() {}

/* ─── INTERACTIVE BACKGROUND ────────────────────── */
function initOrbs() {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const coarsePointer = window.matchMedia('(pointer: coarse)').matches;
  // Skip entirely on touch devices — biggest single perf win
  if (reduceMotion || coarsePointer) return;

  const canvas = document.createElement('canvas');
  canvas.id = 'wb-bg-canvas';
  canvas.setAttribute('aria-hidden', 'true');
  document.body.insertBefore(canvas, document.body.firstChild);
  const ctx = canvas.getContext('2d', { alpha: true, desynchronized: true });
  if (!ctx) return;

  const onPlansPage = Boolean(document.getElementById('wbp-pricing'));
  const GRID = onPlansPage ? 72 : 62;         // fewer grid points
  const R_INFLUENCE = 240;                     // smaller radius = fewer dots computed
  const R_INFLUENCE_SQ = R_INFLUENCE * R_INFLUENCE;
  const MAX_SHIFT = 28;
  const PARTICLE_COUNT = onPlansPage ? 14 : 22;
  // Cap at 24fps — plenty for a background effect
  const FRAME_MS = 1000 / (onPlansPage ? 16 : 24);

  let W = 0, H = 0, raf = 0, lastFrame = 0;
  let isScrolling = false, scrollIdleTimer = 0;
  let particles = [];
  let darkSectionRects = [];

  // Pre-allocated reusable values
  let tmx = -999, tmy = -999, smx = -999, smy = -999;

  // Throttle mousemove to rAF cadence using a flag
  let mouseScheduled = false;
  let rawMX = -999, rawMY = -999;
  window.addEventListener('mousemove', e => {
    rawMX = e.clientX; rawMY = e.clientY;
    if (!mouseScheduled) {
      mouseScheduled = true;
      requestAnimationFrame(() => {
        tmx = rawMX; tmy = rawMY;
        mouseScheduled = false;
      });
    }
  }, { passive: true });

  function findDarkSections() {
    const els = document.querySelectorAll(
      '.section-dark,.section-ink,.wb-trust-section,.wbp-final,footer'
    );
    cacheDarkRects(els);
  }

  function cacheDarkRects(els) {
    darkSectionRects = [];
    els = els || document.querySelectorAll(
      '.section-dark,.section-ink,.wb-trust-section,.wbp-final,footer'
    );
    for (let i = 0; i < els.length; i++) {
      const r = els[i].getBoundingClientRect();
      if (r.bottom >= 0 && r.top <= H) {
        darkSectionRects.push(r.top, r.bottom); // flat array, faster iteration
      }
    }
  }

  function sectionIsDark(y) {
    for (let i = 0; i < darkSectionRects.length; i += 2) {
      if (y >= darkSectionRects[i] && y <= darkSectionRects[i + 1]) return true;
    }
    return false;
  }

  function createParticles() {
    particles = [];
    for (let i = 0; i < PARTICLE_COUNT; i++) {
      particles.push({
        x: Math.random() * W, y: Math.random() * H,
        r: Math.random() * 1.4 + 0.4,
        vx: (Math.random() - 0.5) * 0.2,
        vy: (Math.random() - 0.5) * 0.2,
        alpha: Math.random() * 0.13 + 0.04,
        lime: Math.random() < 0.3
      });
    }
  }

  function resize() {
    W = canvas.width = window.innerWidth;
    H = canvas.height = window.innerHeight;
    createParticles();
    findDarkSections();
  }

  // Pre-compute string lookups to avoid repeated toFixed calls in hot path
  const LIME_PRE = 'rgba(184,245,72,';
  const WHITE_PRE = 'rgba(255,255,255,';
  const INK_PRE = 'rgba(15,14,13,';

  // Reusable alpha string cache (0.00 – 0.70 in steps of 0.01)
  const alphaCache = {};
  function a(v) {
    const k = (v * 100 | 0);
    return alphaCache[k] || (alphaCache[k] = (k / 100).toFixed(2));
  }

  function draw(now) {
    raf = requestAnimationFrame(draw);
    if (now - lastFrame < FRAME_MS) return;
    lastFrame = now;
    if (isScrolling) return;

    ctx.clearRect(0, 0, W, H);

    if (tmx > 0) {
      smx += (tmx - smx) * 0.07;
      smy += (tmy - smy) * 0.07;
    }
    const mx = smx, my = smy;
    const cols = Math.ceil(W / GRID) + 1;
    const rows = Math.ceil(H / GRID) + 1;

    // Only run the expensive influence calc if mouse is on screen
    const mouseActive = mx > 0;

    for (let r = 0; r < rows; r++) {
      const by = r * GRID;
      const dark = sectionIsDark(by);
      const baseAlpha = dark ? 0.1 : 0.065;

      for (let c = 0; c < cols; c++) {
        const bx = c * GRID;
        let px = bx, py = by, alpha = baseAlpha, rad = 1.1, lime = false;

        if (mouseActive) {
          const dx = bx - mx, dy = by - my;
          const distSq = dx * dx + dy * dy;
          if (distSq < R_INFLUENCE_SQ) {
            const dist = Math.sqrt(distSq);
            const str = 1 - dist / R_INFLUENCE;
            const ease = str * str;
            const angle = Math.atan2(dy, dx);
            px = bx + Math.cos(angle) * ease * MAX_SHIFT;
            py = by + Math.sin(angle) * ease * MAX_SHIFT;
            alpha = baseAlpha + ease * (dark ? 0.55 : 0.48);
            rad = 1.1 + ease * 3.2;
            lime = ease > 0.4;
          }
        }

        ctx.beginPath();
        ctx.arc(px, py, rad, 0, 6.2832);
        ctx.fillStyle = lime
          ? LIME_PRE + a(Math.min(alpha * 1.6, 0.85)) + ')'
          : dark
            ? WHITE_PRE + a(alpha) + ')'
            : INK_PRE + a(alpha) + ')';
        ctx.fill();
      }
    }

    // Connection lines — only near cursor, skip if no mouse
    if (mouseActive) {
      const lr = R_INFLUENCE * 0.6;
      const lrSq = lr * lr;
      const nearby = [];
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const bx = c * GRID, by = r * GRID;
          const dx = bx - mx, dy = by - my;
          const dSq = dx * dx + dy * dy;
          if (dSq < lrSq) nearby.push(bx, by, Math.sqrt(dSq)); // flat array
        }
      }
      const lim = Math.min(nearby.length / 3, 10);
      const gLim = (GRID * 1.5) * (GRID * 1.5);
      for (let i = 0; i < lim; i++) {
        for (let j = i + 1; j < lim; j++) {
          const dx = nearby[i*3] - nearby[j*3];
          const dy = nearby[i*3+1] - nearby[j*3+1];
          if (dx*dx + dy*dy < gLim) {
            const aa = (1 - (nearby[i*3+2] + nearby[j*3+2]) / 2 / lr) * 0.22;
            ctx.beginPath();
            ctx.moveTo(nearby[i*3], nearby[i*3+1]);
            ctx.lineTo(nearby[j*3], nearby[j*3+1]);
            ctx.strokeStyle = LIME_PRE + a(aa) + ')';
            ctx.lineWidth = 0.7;
            ctx.stroke();
          }
        }
      }
    }

    // Particles
    for (let i = 0; i < particles.length; i++) {
      const p = particles[i];
      p.x += p.vx; p.y += p.vy;
      if (p.x < -10) p.x = W + 10;
      else if (p.x > W + 10) p.x = -10;
      if (p.y < -10) p.y = H + 10;
      else if (p.y > H + 10) p.y = -10;
      const dark = sectionIsDark(p.y);
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.r, 0, 6.2832);
      ctx.fillStyle = p.lime
        ? LIME_PRE + p.alpha + ')'
        : dark
          ? WHITE_PRE + a(p.alpha * 0.6) + ')'
          : INK_PRE + p.alpha + ')';
      ctx.fill();
    }
  }

  let resizeTimer;
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(resize, 120);
  }, { passive: true });

  let scrollScheduled = false;
  window.addEventListener('scroll', () => {
    isScrolling = true;
    clearTimeout(scrollIdleTimer);
    scrollIdleTimer = setTimeout(() => { isScrolling = false; }, 140);
    if (!scrollScheduled) {
      scrollScheduled = true;
      requestAnimationFrame(() => { cacheDarkRects(); scrollScheduled = false; });
    }
  }, { passive: true });

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) {
      cancelAnimationFrame(raf); raf = 0;
    } else if (!raf) {
      lastFrame = 0; raf = requestAnimationFrame(draw);
    }
  });

  resize();
  raf = requestAnimationFrame(draw);
}

/* ─── MAGNETIC BUTTONS ───────────────────────────── */
function initMagnetic() {
  if (window.matchMedia('(pointer: coarse)').matches) return;
  document.querySelectorAll('.btn-primary,.btn-outline,.btn-ghost,.btn-mockup-trigger').forEach(btn => {
    btn.classList.add('magnetic');
    btn.addEventListener('mousemove', e => {
      const r = btn.getBoundingClientRect();
      const dx = (e.clientX - r.left - r.width  / 2) * 0.12;
      const dy = (e.clientY - r.top  - r.height / 2) * 0.12;
      btn.style.transform = `translate(${dx}px,${dy}px)`;
    });
    btn.addEventListener('mouseleave', () => { btn.style.transform = ''; });
  });
}

/* ─── OFFER CARD MOUSE GLOW ──────────────────────── */
function initCardGlow() {
  document.querySelectorAll('.offer-card').forEach(card => {
    card.addEventListener('mousemove', e => {
      const r = card.getBoundingClientRect();
      card.style.setProperty('--mouse-x', ((e.clientX - r.left) / r.width  * 100).toFixed(1) + '%');
      card.style.setProperty('--mouse-y', ((e.clientY - r.top)  / r.height * 100).toFixed(1) + '%');
    });
  });
}

/* ─── 3D TILT ON PLAN CARDS ──────────────────────── */
function initTilt() {
  if (window.matchMedia('(pointer: coarse)').matches) return;
  document.querySelectorAll('.plan-mini-card,.work-thumb').forEach(el => {
    el.classList.add('tilt-card');
    el.addEventListener('mousemove', e => {
      const r = el.getBoundingClientRect();
      const rx = (e.clientY - r.top  - r.height / 2) / r.height * -10;
      const ry = (e.clientX - r.left - r.width  / 2) / r.width  *  10;
      el.style.transform = `perspective(800px) rotateX(${rx}deg) rotateY(${ry}deg) translateY(-6px)`;
    });
    el.addEventListener('mouseleave', () => { el.style.transform = ''; });
  });
}

/* ─── SCROLL PARALLAX ON HERO VISUAL ─────────────── */
function initParallax() {
  const heroVisual = document.querySelector('.hero-visual');
  const heroBg     = document.querySelector('.hero-bg');
  if (!heroVisual) return;
  let ticking = false;
  window.addEventListener('scroll', () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      const y = window.scrollY;
      heroVisual.style.transform = `translateY(${y * 0.15}px)`;
      if (heroBg) heroBg.style.transform = `translateY(${y * 0.08}px)`;
      ticking = false;
    });
  }, { passive: true });
}

/* ─── SCROLL REVEAL (single unified observer) ─────── */
function initReveal() {
  const selectors = [
    '.reveal','.reveal-left','.reveal-right','.reveal-scale',
    '.section-fade','.clip-reveal','.slide-left','.slide-right',
    '.stat-pop','.stagger-children','.eyebrow-draw','.highlight-wipe',
    '.work-case','.process-step','.wb-stat-card',
    '.offer-grid','.plans-preview-row'
  ];
  const els = document.querySelectorAll(selectors.join(','));
  if (!els.length) return;

  const obs = new IntersectionObserver(entries => {
    entries.forEach(e => {
      if (!e.isIntersecting) return;
      const el = e.target;
      const delay = parseInt(el.dataset.delay || el.style.getPropertyValue('--delay') || 0);
      if (delay) {
        setTimeout(() => el.classList.add('visible', 'animated'), delay);
      } else {
        el.classList.add('visible', 'animated');
      }
      obs.unobserve(el);
    });
  }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });

  els.forEach(el => obs.observe(el));
}

/* ─── PROC-HERO INIT ─────────────────────────────── */
function initProcHero() {
  const hero = document.querySelector('.proc-hero');
  if (!hero) return;
  requestAnimationFrame(() => {
    hero.classList.add('proc-hero-ready');
    setTimeout(() => {
      hero.querySelectorAll('.proc-sc-fill').forEach(el => {
        const w = el.style.width;
        el.style.width = '0';
        requestAnimationFrame(() => requestAnimationFrame(() => { el.style.width = w; }));
      });
    }, 400);
  });
}

/* ─── COUNTER ANIMATE ────────────────────────────── */
function initCounters() {
  const els = document.querySelectorAll('[data-count]');
  if (!els.length) return;
  const obs = new IntersectionObserver(entries => {
    entries.forEach(e => {
      if (!e.isIntersecting) return;
      const el = e.target;
      const target = parseInt(el.dataset.count);
      const suffix = el.dataset.suffix || '';
      const start = performance.now();
      const dur = 1300;
      (function tick(now) {
        const prog = Math.min((now - start) / dur, 1);
        el.textContent = Math.floor((1 - Math.pow(1 - prog, 4)) * target) + suffix;
        if (prog < 1) requestAnimationFrame(tick);
      })(start);
      obs.unobserve(el);
    });
  }, { threshold: 0.5 });
  els.forEach(el => obs.observe(el));
}

/* ─── WORD SPLIT ANIMATION ───────────────────────── */
function initWordSplit() {
  document.querySelectorAll('.split-words').forEach(el => {
    el.innerHTML = el.textContent.split(' ').map(w =>
      `<span class="word"><span class="word-inner">${w}</span></span>`
    ).join(' ');
  });
}

/* ─── MARQUEE INJECTION ──────────────────────────── */
function initMarquee() {
  const target = document.querySelector('.wb-marquee-inject');
  if (!target) return;
  const items = [
    'Event Photography','Beauty Studio','Tattoo & Ink','Restaurant',
    'Premium Portfolio','Barbershop Booking','Free mockup in 48h',
    'No calls needed','Mobile-first builds','Live in under a week',
    'Hosting included','Booking integration'
  ];
  const track = document.createElement('div');
  track.className = 'wb-marquee-track';
  [...items, ...items].forEach(text => {
    const item = document.createElement('span');
    item.className = 'wb-marquee-item';
    const dot = document.createElement('span');
    dot.className = 'wb-marquee-dot';
    item.appendChild(dot);
    item.appendChild(document.createTextNode(text));
    track.appendChild(item);
  });
  target.appendChild(track);
}

/* ─── WORK STRIP INJECTION ───────────────────────── */
function initWorkStrip() {
  const target = document.querySelector('.wb-strip-inject');
  if (!target) return;
  const sites = [
    { img: 'site-photography.png', label: 'Event Photography',   url: 'https://eventphotographer.pages.dev/' },
    { img: 'site-beauty.png',      label: 'Luna Beauty Studio',  url: 'https://lunabeauty.pages.dev/' },
    { img: 'site-barber.png',      label: "Marbello's Barbershop", url: 'https://marbellos3.pages.dev/' },
    { img: 'site-events.png',      label: 'Event Photo & Video', url: 'https://eventphotographer.pages.dev/' },
    { img: 'site-beauty.png',      label: 'Vale Ink Tattoo',     url: 'https://valeink.pages.dev/' },
    { img: 'site-barber.png',      label: "Barber Rochello's",   url: 'https://barberbooking.pages.dev/' },
  ];
  const assetBase = target.dataset.assetBase || window.WB_ASSET_BASE || '';
  const track = document.createElement('div');
  track.className = 'wb-strip-track';
  [...sites, ...sites].forEach(site => {
    const item = document.createElement('div');
    item.className = 'wb-strip-item';
    item.onclick = () => window.open(site.url, '_blank');
    const img = document.createElement('img');
    img.src = assetBase + site.img;
    img.alt = site.label;
    img.loading = 'lazy';
    const label = document.createElement('div');
    label.className = 'wb-strip-label';
    const dot = document.createElement('span');
    dot.className = 'wb-strip-dot';
    label.appendChild(dot);
    label.appendChild(document.createTextNode(site.label));
    item.appendChild(img);
    item.appendChild(label);
    track.appendChild(item);
  });
  target.appendChild(track);
}

/* ─── NAV LIVE DOT ───────────────────────────────── */
function initNavLiveDot() {
  const logo = document.querySelector('.nav-logo');
  if (!logo) return;
  const dot = document.createElement('span');
  dot.className = 'nav-live-dot';
  dot.style.marginLeft = '6px';
  logo.appendChild(dot);
}

/* ─── INIT ALL ───────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  initMagnetic();
  initCardGlow();
  initTilt();
  initParallax();
  initReveal();
  initProcHero();
  initNavLiveDot();
  initCounters();

  const startOrbs = () => initOrbs();
  if (document.body?.dataset?.oniHome === 'true') {
    const runWhenIdle = () => {
      if ('requestIdleCallback' in window) requestIdleCallback(startOrbs, { timeout: 1400 });
      else setTimeout(startOrbs, 700);
    };
    if (document.readyState === 'complete') runWhenIdle();
    else window.addEventListener('load', runWhenIdle, { once: true });
  } else {
    startOrbs();
  }
});
